import { Popover } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'
import { useAuth } from '@/entities/auth/model'
import { ExpandViewIcon } from '@/shared/ui/icons'
import { useMutation } from '@tanstack/react-query'
import { useRequest } from '@/shared/api'
import { Toast } from '@/shared/ui/organisms/toast'
import { getCookie } from '@/shared/lib/formatter'

interface Notification {
  id: string | number
  message?: string
  read?: boolean
  params?: Record<string, string>
}

export interface NotificationPopupModalProps {
  [key: string]: any;
  children: React.ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  notifications?: Notification[]
  refetch?: () => void
}

export function NotificationPopupModal({
  children,
  open,
  onOpenChange,
  notifications,
  refetch,
}: NotificationPopupModalProps) {
  const navigate = useSafeNavigate()
  const auth = useAuth()
  const { t } = useTranslation()
  const { request } = useRequest()

  const postSave = useMutation<any, any, any>({
    mutationFn: ({ url }: { url: string }) => {
      return request({ url, method: 'POST' })
    },
  })

  const onSave = ({ id, link }: { id: string | number; link?: string }) => {
    if (link) {
      onOpenChange(false)
      try {
        const url = new URL(link)
        navigate(url.pathname + url.search)
      } catch {
        if (link.startsWith('/')) {
          navigate(link)
        }
      }
    }

    postSave.mutate(
      { url: `/v1/notifications/${id}/read` },
      {
        onSuccess: (res: { success?: boolean; message?: string }) => {
          if (res.success) {
            refetch?.()
          }
        },
      }
    )
  }

  const content = (
    <div className="flex flex-col">
      {notifications
        ?.filter((item) => !item?.read)
        ?.slice(0, 10)
        ?.map((notification) => (
          <p
            key={notification?.id}
            className="m-0 py-2.5 px-5 cursor-pointer flex items-center gap-[5px] text-[13px] font-normal w-[300px] justify-between hover:bg-(--primary-btn-light-bg) text-(--main-title-color)"
            onClick={() =>
              onSave({
                id: notification?.id,
                link: notification?.params?.['event-link'],
              })
            }
          >
            <div className="w-60">{notification?.message}</div>
            <ExpandViewIcon color="rgba(33, 33, 33, 1)" width="18" height="18" />
          </p>
        ))}
      <p
        className="m-0 py-2.5 px-5 cursor-pointer flex items-center gap-[5px] text-[13px] font-normal w-[300px] justify-between hover:bg-(--primary-btn-light-bg) !text-[#0078D4]"
        onClick={() => {
          onOpenChange(false)
          navigate(`/user/notifications?read=ALL&user_id=${getCookie('userId')}`)
        }}
      >
        <div>{t('all_notifications')}</div>
      </p>
    </div>
  )

  return (
    <div className="[&_.ant-popover]:!w-[344px] [&_.ant-popover-content]:!w-[344px] [&_.ant-popover-content>.ant-popover-inner]:!p-[15px_22px] [&_.ant-popover-content>.ant-popover-inner]:!w-[344px]">
      <Popover
        className="p-0 [&.ant-popover-inner]:!p-[15px_22px] [&.ant-popover-inner]:!w-[344px]"
        content={content}
        title={<div className="ml-5">{t('notifications')}</div>}
        trigger="click"
        open={open}
        onOpenChange={onOpenChange}
      >
        {children}
      </Popover>
    </div>
  )
}
